import React, { useEffect, useState } from 'react';
import { ApiError, api } from '@/lib/api';
import { useAuth } from '@/auth/AuthContext';

/**
 * Глобальный замок на изменение исторических данных.
 *
 * Управление настройкой может только супер-администратор — на сервере
 * есть дополнительная проверка `is_super_admin = 1` сверх прав
 * `settings.data_lock/write`. Любой другой админ видит панель в режиме
 * read-only.
 *
 * При включённом замке (enabled + lock_date) на бэке проверяются:
 *   • Договоры (дата заключения, расторжение).
 *   • Платежи (дата платежа).
 *   • Начисления — основные и доп. (дата месяца).
 *   • Журнал, расписание — следующим этапом.
 *
 * Если объект попадает в закрытый период, API возвращает 422 с кодом
 * `locked` и человеческим сообщением — фронт показывает его как обычно.
 */
export default function DataLockPanel() {
  const { user, permissions } = useAuth();
  const isSuperAdmin = !!permissions.isSuperAdmin;

  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);
  const [enabled, setEnabled] = useState('off');
  const [lockDate, setLockDate] = useState('');
  const [allowTermination, setAllowTermination] = useState('off');
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);

  useEffect(() => {
    let aborted = false;
    setLoading(true);
    setError(null);
    api.get('/api/settings/data_lock')
      .then((res) => {
        if (aborted) return;
        const v = res.value || {};
        setEnabled(v.enabled || 'off');
        setLockDate(v.lock_date || '');
        setAllowTermination(v.allow_termination || 'off');
      })
      .catch((e) => {
        if (aborted) return;
        setError(e instanceof ApiError ? e.message : 'Не удалось загрузить настройки');
      })
      .finally(() => { if (!aborted) setLoading(false); });
    return () => { aborted = true; };
  }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!isSuperAdmin) return;
    if (enabled === 'on' && !/^\d{4}-\d{2}-\d{2}$/.test(lockDate)) {
      setError('Укажите дату замка');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const res = await api.put('/api/settings/data_lock', {
        value: { enabled, lock_date: lockDate || '', allow_termination: allowTermination },
      });
      const v = res.value || {};
      setEnabled(v.enabled || 'off');
      setLockDate(v.lock_date || '');
      setAllowTermination(v.allow_termination || 'off');
      setSavedAt(new Date());
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Не удалось сохранить');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="settings-card">Загрузка…</div>;

  return (
    <form className="settings-card" onSubmit={onSubmit}>
      <div className="settings-card__head">
        <h2 className="settings-card__title">Запрет изменения данных</h2>
        <p className="settings-card__sub">
          После включения никто не сможет создавать, изменять или удалять
          объекты с датой <b>не позже</b> указанной — договоры, платежи,
          начисления, оценки, проведённые занятия. Это нужно, чтобы
          стабилизировать отчёты и выгрузки за закрытый период.
        </p>
      </div>

      {!isSuperAdmin && (
        <div style={{
          margin: '12px 20px 0',
          padding: '10px 12px',
          fontSize: 12.5,
          border: '1px solid var(--border)',
          borderRadius: 6,
          background: 'var(--panel-2, #f6f6f6)',
          color: 'var(--fg-muted)',
        }}>
          Менять замок может только супер-администратор. У вас режим просмотра.
        </div>
      )}

      <div className="settings-grid">
        <div className="settings-field">
          <label className="settings-field__label">Замок</label>
          <select
            className="settings-field__input"
            value={enabled}
            onChange={(e) => { setEnabled(e.target.value); setSavedAt(null); }}
            disabled={!isSuperAdmin}
          >
            <option value="off">Выключен</option>
            <option value="on">Включён</option>
          </select>
          <div className="settings-field__hint">
            {enabled === 'on'
              ? 'Изменения и создание объектов в закрытый период будут отклоняться с ошибкой.'
              : 'Все изменения разрешены, включая правки за прошлые периоды.'}
          </div>
        </div>

        <div className="settings-field">
          <label className="settings-field__label">
            Дата замка (включительно)
          </label>
          <input
            type="date"
            className="settings-field__input"
            value={lockDate}
            onChange={(e) => { setLockDate(e.target.value); setSavedAt(null); }}
            disabled={!isSuperAdmin}
          />
          <div className="settings-field__hint">
            Закрыт всё, что <b>≤ этой даты</b>. Например, замок на 31.12.2025
            запрещает редактирование любых объектов, относящихся к 2025 году
            и более ранним.
          </div>
        </div>

        <div className="settings-field" style={{ gridColumn: '1 / -1' }}>
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: isSuperAdmin ? 'pointer' : 'default' }}>
            <input
              type="checkbox"
              checked={allowTermination === 'on'}
              onChange={(e) => { setAllowTermination(e.target.checked ? 'on' : 'off'); setSavedAt(null); }}
              disabled={!isSuperAdmin}
            />
            <span>Разрешить досрочное расторжение договоров</span>
          </label>
          <div className="settings-field__hint">
            Договор из закрытого периода можно будет расторгнуть, а также
            изменить или убрать расторжение — но только если дата расторжения
            (и старая, и новая) <b>позже даты замка</b>. Остальные поля
            договора остаются под запретом; закрытые данные не меняются.
          </div>
        </div>
      </div>

      <div className="settings-card__foot">
        {error && <div style={{ color: 'var(--red-fg)', fontSize: 12.5 }}>{error}</div>}
        {savedAt && !error && (
          <div style={{ color: 'var(--green-700)', fontSize: 12.5 }}>
            Сохранено в {savedAt.toLocaleTimeString('ru-RU')}
          </div>
        )}
        <div className="settings-card__spacer" />
        <button
          type="submit"
          className="btn btn--primary btn--sm"
          disabled={!isSuperAdmin || saving}
          title={isSuperAdmin ? undefined : 'Только супер-администратор'}
        >
          {saving ? 'Сохраняем…' : 'Сохранить'}
        </button>
      </div>
    </form>
  );
}
